import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/apiClient";
import { toast } from "sonner";

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", hotel_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome ${user.name}! Your property "${form.hotel_name}" is ready.`);
      navigate("/dashboard");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex bg-[#0B101E]" data-testid="signup-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://images.pexels.com/photos/32978233/pexels-photo-32978233.jpeg" alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B101E]/60 to-[#0B101E]/90" />
        <div className="relative z-10 p-16 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-md bg-[#D4AF37] flex items-center justify-center text-[#0B101E] font-bold font-display">B</span>
            <span className="font-display text-2xl">Booking4U</span>
          </Link>
          <div>
            <h2 className="font-display text-4xl leading-tight max-w-md">
              Launch your own property dashboard in <span className="gold-shine">under 60 seconds</span>.
            </h2>
            <ul className="mt-6 text-sm text-white/65 space-y-2">
              <li>· Free 14-day trial, no card required</li>
              <li>· Own login + full dashboard scoped to your property</li>
              <li>· Channel manager, booking engine, AI revenue manager</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Get started</div>
          <h1 className="font-display text-4xl mb-2">Create your property</h1>
          <p className="text-white/60 text-sm mb-8">You'll be the admin for this property. We provision your dashboard instantly.</p>
          <form onSubmit={onSubmit} className="space-y-4">
            <Input label="Your name" value={form.name} onChange={(v) => upd("name", v)} testid="signup-name" />
            <Input label="Property name" value={form.hotel_name} onChange={(v) => upd("hotel_name", v)} testid="signup-hotel-name" />
            <Input label="Email" type="email" value={form.email} onChange={(v) => upd("email", v)} testid="signup-email" />
            <Input label="Password (min 6 chars)" type="password" value={form.password} onChange={(v) => upd("password", v)} testid="signup-password" />
            {error && <div className="text-sm text-red-400" data-testid="signup-error">{error}</div>}
            <button type="submit" disabled={loading} className="btn-gold w-full rounded-md py-3 disabled:opacity-60" data-testid="signup-submit">
              {loading ? "Provisioning…" : "Create my property"}
            </button>
          </form>
          <div className="mt-8 text-sm text-white/50 text-center">
            Already have an account? <Link to="/login" className="text-[#D4AF37] hover:text-[#F2D06B]">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50">{label}</label>
      <input
        type={type} required minLength={type === "password" ? 6 : undefined}
        value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-2 w-full bg-[#12182B] border border-white/10 rounded-md px-4 py-3 focus:outline-none focus:border-[#D4AF37]/60 transition"
      />
    </div>
  );
}
